import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import DramaInfo from '@/pages/drama-detail/components/DramaInfo';
import EpisodeSection from '@/pages/drama-detail/components/EpisodeSection';
import CharacterSection from '@/pages/drama-detail/components/CharacterSection';
import { dramas } from '@/mocks/dramas';
import { newsList } from '@/mocks/news';
import { useFavorites } from '@/hooks/useFavorites';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useJsonLd } from '@/hooks/useJsonLd';

export default function DramaDetailPage() {
  const { id = '' } = useParams();
  const drama = dramas.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();

  const related = useMemo(() => {
    if (!drama) return [];
    return dramas
      .filter((item) => item.id !== drama.id && item.genres.some((g) => drama.genres.includes(g)))
      .sort((a, b) => b.hot - a.hot)
      .slice(0, 5);
  }, [drama]);

  const relatedNews = useMemo(
    () => newsList.filter((item) => item.relatedDramaId === id).slice(0, 3),
    [id],
  );

  usePageMeta(
    drama ? `${drama.title} - 详细剧情与资讯` : '剧集未找到 - 我妻子的一切',
    drama
      ? `《${drama.title}》剧情介绍、角色资料与分集信息。${drama.synopsis}`
      : '未找到对应的剧集。',
    drama ? `${drama.title},${drama.genres.join(',')},剧情介绍,角色资料,分集信息` : '',
  );

  useJsonLd(
    'ld-drama',
    drama
      ? JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'TVSeries',
          name: drama.title,
          description: drama.synopsis,
          image: drama.cover,
          director: { '@type': 'Person', name: drama.director },
          actor: drama.cast.map((name) => ({ '@type': 'Person', name })),
          numberOfEpisodes: drama.episodes,
          genre: drama.genres,
          datePublished: drama.firstAirDate,
          inLanguage: 'zh-CN',
          url: `https://lfivu.cn/detail/tv/${drama.id}`,
        })
      : null,
  );

  if (!drama) {
    return (
      <div className="mx-auto w-full max-w-site px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-film-line"
          title="没有找到这部剧"
          description="剧集可能已下线或链接有误，返回电视剧分类继续浏览吧。"
          actionLabel="返回电视剧分类"
          actionTo="/category/电视剧"
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-site px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[{ label: '首页', to: '/' }, { label: '电视剧', to: '/category/电视剧' }, { label: drama.title }]}
      />

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <DramaInfo
              drama={drama}
              isFavorite={isFavorite(drama.id)}
              onToggleFavorite={() => toggleFavorite(drama.id)}
            />
          </div>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <div className="flex items-center gap-2">
              <i className="ri-book-open-line text-lg text-primary-500" />
              <h2 className="font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
            </div>
            <p className="mt-3 text-sm leading-loose text-foreground-700">{drama.synopsis}</p>
            <p className="mt-3 text-sm leading-loose text-foreground-700">
              本剧由 {drama.director} 执导，{drama.cast.join('、')} 联袂主演，共 {drama.episodes} 集，
              {drama.status === '连载' ? '目前正在持续更新中。' : '现已全集完结。'}
              剧集以细腻的人物刻画与层层递进的情节见长，适合喜欢深度剧情与人物关系的观众。
            </p>
          </section>

          <CharacterSection characters={drama.characters} />
          <EpisodeSection dramaTitle={drama.title} episodes={drama.episodeList} />
        </div>

        <aside className="space-y-6">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-thumb-up-line text-primary-500" />
              相关推荐
            </h2>
            <div className="mt-4 space-y-3">
              {related.length > 0 ? (
                related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/detail/tv/${item.id}`}
                    className="group flex items-center gap-3"
                  >
                    <span className="h-20 w-14 shrink-0 overflow-hidden rounded-md">
                      <img
                        src={item.cover}
                        alt={item.title}
                        title={`${item.title} - 相关推荐`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-heading text-sm font-bold text-foreground-900 transition-colors group-hover:text-primary-500">
                        {item.title}
                      </span>
                      <span className="mt-1 block truncate text-xs text-foreground-400">
                        {item.genres.slice(0, 2).join(' · ')}
                      </span>
                      <span className="mt-1 flex items-center gap-1 text-xs text-foreground-400">
                        <i className="ri-fire-line text-accent-500" />
                        {item.hot.toLocaleString()}
                      </span>
                    </span>
                  </Link>
                ))
              ) : (
                <p className="text-sm text-foreground-400">暂无相关推荐。</p>
              )}
            </div>
          </section>

          {relatedNews.length > 0 && (
            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-newspaper-line text-primary-500" />
                相关花絮
              </h2>
              <div className="mt-4 space-y-3">
                {relatedNews.map((item) => (
                  <Link
                    key={item.id}
                    to={`/detail/news/${item.id}`}
                    className="block border-b border-background-100 pb-3 last:border-b-0 last:pb-0"
                  >
                    <span className="line-clamp-2 text-sm font-medium text-foreground-800 transition-colors hover:text-primary-500">
                      {item.title}
                    </span>
                    <span className="mt-1 flex items-center gap-1 text-xs text-foreground-400">
                      <i className="ri-time-line" />
                      {item.date}
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}